import React from 'react';
import PropTypes, { InferProps } from 'prop-types';
import { useForm, SubmitHandler } from 'react-hook-form';
import styled from '@emotion/styled';
import Button from '../Button';
import { getEmailValidation } from '../lib/validationRules';
import ValidationError from '../ValidationError';
import { EmailFieldWithController } from '../EmailField';
import breakpoints from '../Theme/breakpoints';
import Headline2 from '../Typography/Heading/Headline2';
import Headline6 from '../Typography/Heading/Headline6';

const newsletterFormPropertyTypes = {
  getSubmitHandler: PropTypes.func.isRequired,
  submitDisabled: PropTypes.bool.isRequired,
  title: PropTypes.string.isRequired,
  description: PropTypes.node.isRequired,
  emailValidationMessage: PropTypes.string.isRequired,
  requiredValidationMessage: PropTypes.string.isRequired,
  emailAddressLabel: PropTypes.string.isRequired,
  formatEmailSuggestion: PropTypes.func.isRequired,
  disclaimerMessage: PropTypes.node.isRequired,
  submitLabel: PropTypes.string.isRequired,
  showDescriptiveText: PropTypes.bool,
  graphqlError: PropTypes.shape({
    message: PropTypes.string,
  }),
};

type NewsletterFormProperties = Omit<InferProps<
  typeof newsletterFormPropertyTypes
>, 'getSubmitHandler'> & {
  getSubmitHandler: (callback: () => void) => SubmitHandler<{ email: string }>;
};

const NewsletterContainer = styled.div`
  font-family: Helvetica Neue, Arial, sans-serif;
  display: flex;
  flex-direction: column;
  margin: auto;
  grid-column-start: 1;
  grid-column-end: 12;
  grid-area: newsletter;

  @media (min-width: ${breakpoints.md}px) {
    flex-direction: row;
  }
  @media print {
    display: none;
  }
`;

const DescriptionContainer = styled.div`
  flex: 1;
  font-size: 1.125rem;

  @media (min-width: ${breakpoints.md}px) {
    padding-right: 2rem;
  }

  h2 {
    margin-bottom: 2rem;
  }

  p {
    color: rgb(107 114 128);
    font-weight: bold;
    line-height: 1.75rem;
    margin-top: 0;
  }

  blockquote {
    font-family: Georgia, Cambria, sans-serif;
    font-style: italic;
    line-height: 2rem;
    border-left: 3px solid rgb(229,231,255);
    margin: 1.5rem 0;
    padding-left: 1rem;
  }
`;

const FormContainer = styled.div`
  margin: auto;

  && {
    .form-item {
      display: flex;
      flex-direction: column;
      align-items: center;

      .MuiFormControl-root {
        width: 100%;
      }
    }
    span {
      align-self: baseline;
    }

    @media (min-width: ${breakpoints.sm}px) {
      flex-direction: row;
      flex: 1;
    }
  }
`;

const FormHeader = styled(Headline6)`
  margin-bottom: 2rem;
`;

const Disclaimer = styled.div`
  margin-top: .75rem;
  margin-bottom: 2rem;
  color: rgb(107, 114, 128);
`;

const NewsletterFormComponent = ({
  getSubmitHandler,
  submitDisabled,
  title,
  description,
  emailValidationMessage,
  requiredValidationMessage,
  emailAddressLabel,
  formatEmailSuggestion,
  disclaimerMessage,
  submitLabel,
  graphqlError,
  showDescriptiveText,
}: NewsletterFormProperties) => {
  const {
    handleSubmit, control, errors, reset,
  } = useForm({
    defaultValues: { email: '' },
  });

  return (
    <NewsletterContainer>
      {(showDescriptiveText ?? true)
      && (
      <DescriptionContainer>
        <Headline2 tag="h2">{title}</Headline2>
        {description}
      </DescriptionContainer>
      )}
      <FormContainer>
        {(showDescriptiveText ?? true)
          ? undefined : <FormHeader tag="h3">{title}</FormHeader>}
        <form onSubmit={handleSubmit(getSubmitHandler(reset))}>
          <div className="form-item">
            <EmailFieldWithController
              name="email"
              required
              control={control}
              type="email"
              autoComplete="email"
              rules={getEmailValidation(
                emailValidationMessage,
                requiredValidationMessage,
              )}
              variant="filled"
              label={emailAddressLabel}
              formatSuggestion={formatEmailSuggestion}
              useStringOnClick
              defaultValue=""
            />
            <ValidationError errors={errors} name="email" />
            <Button
              variant="contained"
              name="subscribe"
              type="submit"
              disabled={submitDisabled}
            >
              {submitLabel}
            </Button>
          </div>
          <Disclaimer>
            {disclaimerMessage}
          </Disclaimer>
        </form>
        { graphqlError
        && (
          <div className="form-item">
            <ValidationError>{ graphqlError.message }</ValidationError>
          </div>
        )}
      </FormContainer>
    </NewsletterContainer>
  );
};

NewsletterFormComponent.propTypes = newsletterFormPropertyTypes;

export default NewsletterFormComponent;
